import { countryToISO } from 'vopay';

export const PLAN_MODEL_KEYS = new Set(['payAsYouGo', 'subscription']);

const CAD_ONLY_PERMISSIONS = new Set([
  'InteracMoneyRequestEnabled',
  'InteracBulkPayoutEnabled',
  'InboundEtransferEnabled',
  'PushToCardEnabled',
]);

export function isPermissionAllowedForMarket(permissionKey: string, currency: string, country: string): boolean {
  if (/^USDEFT(Collect|Send)Enabled$/.test(permissionKey)) return currency === 'USD' && countryToISO(country) === 'CA';

  if (/^EFT(Collect|Send)Enabled$/.test(permissionKey) || CAD_ONLY_PERMISSIONS.has(permissionKey)) {
    return currency === 'CAD' && countryToISO(country) === 'CA';
  }
  return true;
}

// True when a plan-setting section should be rendered for the given market: either it has no
// permission toggles (fee-only section) or at least one of its permissions is allowed for the
// currency+country. Sections whose permissions are all disallowed are hidden entirely so their
// fees are never shown, validated, or submitted.
export function sectionHasCurrencyAllowedPermission(
  permissions: Record<string, unknown>,
  currency: string,
  country: string,
): boolean {
  const permKeys = Object.keys(permissions).filter((key) => !PLAN_MODEL_KEYS.has(key));
  return permKeys.length === 0 || permKeys.some((key) => isPermissionAllowedForMarket(key, currency, country));
}

// A `select` field stores the chosen option's value (e.g. "D365BC"), so read-only views must look the
// human-readable label back up. The API serializes the option list as an object with numeric string
// keys, hence Object.values. Falls back to the raw value if it matches no option.
export function selectOptionLabel(
  options: Record<string, { value: string; label: string }> | undefined,
  value: string,
): string {
  return Object.values(options ?? {}).find((option) => option.value === value)?.label ?? value;
}

// On a USD package the EFT rails run on the USDEFT permission (no separate matrix entry),
// so label them "USD EFT …". No-op for non-USD packages and non-EFT rails.
export function railDisplayLabel(displayName: string, currency: string): string {
  return currency === 'USD' && /^EFT\b/.test(displayName) ? `USD ${displayName}` : displayName;
}
